import {Component, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {finalize} from 'rxjs';
import {GeoLocationService} from '../services/geo-location.service';
import {AlertService} from '../../../shared/services/alert.service';
import {GeoLocation} from '../../../shared/model/geoLocation';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';

@Component({
  selector: 'app-admin-location-edit',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="modal-header">
      <h4 class="modal-title">Modifica Luogo (Admin)</h4>
      <button type="button" class="btn-close" aria-label="Close" (click)="cancelled.emit()"></button>
    </div>
    <div class="modal-body">
      @if (isLoading()) {
        <div class="text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Caricamento...</span>
          </div>
        </div>
      } @else if (locationForm) {
        <form [formGroup]="locationForm" (ngSubmit)="onSave()">
          <div class="row g-3">
            <div class="col-12">
              <label for="formalName" class="form-label">Nome Luogo</label>
              <input type="text" class="form-control" id="formalName" formControlName="formalName">
            </div>
            <div class="col-12 col-md-8">
              <label for="street" class="form-label">Via/Corso/Piazza</label>
              <input type="text" class="form-control" id="street" formControlName="street">
            </div>
            <div class="col-12 col-md-4">
              <label for="houseNumber" class="form-label">N. Civico</label>
              <input type="text" class="form-control" id="houseNumber" formControlName="houseNumber">
            </div>
            <div class="col-12 col-md-6">
              <label for="city" class="form-label">Città</label>
              <input type="text" class="form-control" id="city" formControlName="city">
            </div>
            <div class="col-12 col-md-2">
              <label for="province" class="form-label">Provincia</label>
              <input type="text" class="form-control" id="province" formControlName="province">
            </div>
            <div class="col-12 col-md-4">
              <label for="postcode" class="form-label">CAP</label>
              <input type="text" class="form-control" id="postcode" formControlName="postcode">
            </div>
            <div class="col-12 col-md-6">
              <label for="lat" class="form-label">Latitudine</label>
              <input type="text" class="form-control" id="lat" formControlName="lat">
            </div>
            <div class="col-12 col-md-6">
              <label for="lon" class="form-label">Longitudine</label>
              <input type="text" class="form-control" id="lon" formControlName="lon">
            </div>
          </div>
        </form>
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-outline-secondary" (click)="cancelled.emit()">Annulla</button>
      <button type="button" class="btn btn-primary" [disabled]="isLoading() || locationForm?.invalid" (click)="onSave()">Salva Modifiche</button>
    </div>
  `,
})
export class AdminLocationEditComponent implements OnInit {
  private geoLocationService = inject(GeoLocationService);
  private alertService = inject(AlertService);
  private fb = inject(FormBuilder);

  @Input({required: true}) location!: GeoLocation;
  @Output() saved = new EventEmitter<GeoLocation>();
  @Output() cancelled = new EventEmitter<void>();

  protected isLoading = signal(false);
  protected locationForm!: FormGroup;

  ngOnInit(): void {
    if (!this.location) {
        this.cancelled.emit();
        return;
    }
    this.initForm(this.location);
  }

  private initForm(loc: GeoLocation): void {
      const address = this.geoLocationService.addressAsObject(loc.address);
      this.locationForm = this.fb.group({
          formalName: [loc.formalName || ''],
          street: [loc.street || address?.street || ''],
          houseNumber: [loc.houseNumber || address?.houseNumber || ''],
          city: [loc.city || address?.city || ''],
          province: [loc.province || address?.province || ''],
          postcode: [loc.postcode || address?.postcode || ''],
          lat: [loc.lat || (loc.latitude != null ? String(loc.latitude) : '')],
          lon: [loc.lon || (loc.longitude != null ? String(loc.longitude) : '')],
      });
  }

  protected onSave(): void {
    if (this.locationForm.invalid || !this.location.id) {
        return;
    }

    this.isLoading.set(true);

    const formValue = this.locationForm.value;
    const updatePayload: any = {
        formalName: formValue.formalName,
        lat: formValue.lat,
        lon: formValue.lon,
        address: {
            street: formValue.street,
            houseNumber: formValue.houseNumber,
            city: formValue.city,
            province: formValue.province,
            postcode: formValue.postcode,
            country: 'Italia'
        }
    };

    this.geoLocationService
      .updateLocation(this.location.id, updatePayload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data) {
            this.alertService.showSuccess('Luogo aggiornato con successo');
            this.saved.emit(response.data);
          }
        },
      });
  }
}
